import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Trash2, Eraser } from "lucide-react";
import { api, currentUser } from "@/lib/api";
import { DARK_DIALOG, DARK_INPUT, MfaField, Pill, inr, fmtDate, errMsg } from "./shared";

const SECTIONS = [
  ["bookings", "Test bookings", [["Code", "code"], ["Customer", "customer_name"], ["Status", "status", (v) => <Pill value={v}/>], ["Amount", "amount", inr], ["Created", "created_at", fmtDate]]],
  ["payments", "Mock payments", [["Payment ID", "gateway_payment_id"], ["Booking", "booking_code"], ["Gateway", "gateway"], ["Amount", "amount", inr], ["Status", "status", (v) => <Pill value={v}/>]]],
  ["attempts", "Mock gateway attempts", [["Order ID", "gateway_order_id"], ["Booking", "booking_code"], ["Gateway", "gateway"], ["Amount", "amount", inr], ["Status", "status", (v) => <Pill value={v}/>]]],
];

export default function TestData() {
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/admin/test-data").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-slate-400">Loading…</div>;
  const total = d.counts.bookings + d.counts.payments + d.counts.attempts;
  return (
    <div data-testid="test-data-page" className="max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold">Super Admin · Maintenance</div>
          <h1 className="font-display text-3xl font-bold mt-1">Mock & Test Data</h1>
          <p className="text-sm text-slate-400 mt-1">Mock payments (marked TEST), their gateway attempts and TEST bookings. Real Razorpay payments and bookings with real payments are never listed or removed.</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" disabled={!total} onClick={() => setDlg({ type: "clear" })} data-testid="clear-mock-tests-btn"><Eraser size={14} className="mr-2"/>Clear Mock Tests ({total})</Button>
      </div>
      {SECTIONS.map(([kind, title, cols]) => (
        <section key={kind} className="mb-6" data-testid={`td-section-${kind}`}>
          <div className="font-bold mb-2">{title} <span className="font-mono text-xs text-slate-500" data-testid={`td-count-${kind}`}>{d.counts[kind]}</span></div>
          <div className="admin-card overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead className="bg-slate-800/70 text-slate-400 text-[10px] uppercase tracking-widest"><tr>{cols.map(([h]) => <th key={h} className="p-3 text-left">{h}</th>)}<th></th></tr></thead>
              <tbody>
                {d[kind].map((r) => (
                  <tr key={r.id} className="border-t border-slate-800" data-testid={`td-row-${kind}-${r.id}`}>
                    {cols.map(([h, k, fmt]) => <td key={h} className="p-3 text-xs text-slate-300">{fmt ? fmt(r[k]) : (r[k] || "—")}</td>)}
                    <td className="pr-3 text-right"><Button size="sm" variant="ghost" className="text-red-300 h-8" onClick={() => setDlg({ type: "one", kind, r })} data-testid={`td-remove-${kind}-${r.id}`}><Trash2 size={13} className="mr-1"/>Remove</Button></td>
                  </tr>
                ))}
                {!d[kind].length && <tr><td colSpan={cols.length + 1} className="p-6 text-center text-slate-500 text-xs" data-testid={`td-empty-${kind}`}>None</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      <ResetCard onDone={load}/>
      {dlg && <ConfirmDialog dlg={dlg} counts={d.counts} phrase={d.confirm_phrase} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }}/>}
    </div>
  );
}

function ConfirmDialog({ dlg, counts, phrase, onClose, onDone }) {
  const [mfa, setMfa] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const all = dlg.type === "clear";
  const ok = mfa.length === 6 && (!all || text.trim() === phrase);
  const go = async () => {
    setBusy(true);
    try {
      if (all) await api.post("/admin/test-data/clear", { confirm: text, mfa_code: mfa });
      else await api.delete(`/admin/test-data/${dlg.kind}/${dlg.r.id}`, { params: { mfa_code: mfa } });
      toast.success(all ? "All mock/test data cleared" : "Removed"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DARK_DIALOG} max-w-md`} data-testid="td-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{all ? "Clear all mock & test data?" : "Remove this item?"}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {all ? `This permanently deletes ${counts.bookings} test booking(s) with their assignments, ${counts.payments} mock payment(s) and ${counts.attempts} mock attempt(s). Real payments are not affected.`
              : dlg.kind === "bookings" ? `Booking ${dlg.r.code} and its mock payments/assignments will be permanently deleted.` : "This mock record will be permanently deleted."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {all && <div><label className="text-xs font-semibold text-slate-400">Type <b className="text-slate-200">{phrase}</b> to confirm</label><Input value={text} onChange={(e) => setText(e.target.value)} className={`${DARK_INPUT} mt-1 font-mono`} data-testid="td-confirm-phrase"/></div>}
          <MfaField value={mfa} onChange={setMfa} testid="td-mfa"/>
          <div className="flex justify-end"><Button className="bg-red-600 hover:bg-red-700" disabled={!ok || busy} onClick={go} data-testid="td-confirm-btn">{all ? "Clear everything listed" : "Remove"}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetCard({ onDone }) {
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({ confirm: "", current_password: "", mfa_code: "", include_franchises: false });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/reset-operational-data").then((r) => setP(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!p || !currentUser()?.is_primary_super_admin) return null;
  const sum = (o) => Object.values(o || {}).reduce((a, b) => a + b, 0);
  const total = sum(p.counts) + (v.include_franchises ? sum(p.franchise_counts) : 0);
  const go = async () => {
    setBusy(true);
    try { await api.post("/admin/reset-operational-data", v); toast.success("Operational data reset — dashboard is now at zero"); setOpen(false); load(); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="admin-card p-5 mt-8 border-red-500/30" data-testid="reset-ops-card">
      <div className="font-bold text-red-300">Reset to launch state (zero dashboard)</div>
      <p className="text-xs text-slate-400 mt-1">Deletes ALL bookings, payments, attempts, refunds, assignments, payouts, complaints, notifications, ratings, workers and customers. Keeps: {p.keys}. Only available while there are no real Razorpay payments.</p>
      <div className="flex flex-wrap gap-2 mt-3 text-[11px]" data-testid="reset-ops-counts">
        {Object.entries(p.counts).map(([k, n]) => <span key={k} className="rounded border border-slate-700 px-2 py-1 text-slate-300">{k.replace(/_/g, " ")}: <b className="font-mono">{n}</b></span>)}
      </div>
      <label className="flex items-center gap-2 mt-3 text-xs text-slate-300 cursor-pointer">
        <input type="checkbox" checked={v.include_franchises} onChange={(e) => setV({ ...v, include_franchises: e.target.checked })} className="accent-red-500" data-testid="reset-ops-include-franchises"/>
        Also delete all franchises, franchise admins, fee payments and agreements ({sum(p.franchise_counts)} records)
      </label>
      {!p.allowed && <p className="text-xs text-amber-300 mt-2" data-testid="reset-ops-blocked">{p.real_payments} real payment(s) exist — reset disabled.</p>}
      <Button className="bg-red-600 hover:bg-red-700 mt-4" disabled={!p.allowed || !total} onClick={() => setOpen(true)} data-testid="reset-ops-btn"><Eraser size={14} className="mr-2"/>Reset operational data ({total})</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`${DARK_DIALOG} max-w-md`} data-testid="reset-ops-dialog">
          <DialogHeader><DialogTitle>Reset all operational data?</DialogTitle><DialogDescription className="text-slate-400">Permanently deletes {total} records. This cannot be undone.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-semibold text-slate-400">Type <b className="text-slate-200">{p.confirm_phrase}</b></label><Input value={v.confirm} onChange={(e) => setV({ ...v, confirm: e.target.value })} className={`${DARK_INPUT} mt-1 font-mono`} data-testid="reset-ops-phrase"/></div>
            <div><label className="text-xs font-semibold text-slate-400">Your current password</label><Input type="password" value={v.current_password} onChange={(e) => setV({ ...v, current_password: e.target.value })} className={`${DARK_INPUT} mt-1`} data-testid="reset-ops-password"/></div>
            <MfaField value={v.mfa_code} onChange={(c) => setV({ ...v, mfa_code: c })} testid="reset-ops-mfa"/>
            <div className="flex justify-end"><Button className="bg-red-600 hover:bg-red-700" disabled={busy || v.confirm.trim() !== p.confirm_phrase || !v.current_password || v.mfa_code.length !== 6} onClick={go} data-testid="reset-ops-confirm">Reset everything</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
